import { useEffect, useState } from 'react';
import { X, UserPlus, Users } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { logger } from '@/utils/logger';

interface InviteViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ViewerRow {
  email: string;
  role: 'viewer' | 'admin';
  is_active: boolean;
  created_at: string;
  last_invited_at: string | null;
}

/**
 * InviteViewerModal - Admin tool for managing viewers
 * 
 * Features:
 * - Invite tab: Send magic link invitations to new viewers
 * - Manage tab: List, activate, deactivate, resend invites, or delete viewers
 * - Real-time viewer list with status indicators
 * - 10-second timeout for API calls to prevent infinite loading
 */
export default function InviteViewerModal({ isOpen, onClose }: InviteViewerModalProps) {
  const [tab, setTab] = useState<'invite' | 'manage'>('invite');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [rows, setRows] = useState<ViewerRow[]>([]);
  const [isLoadingCount, setIsLoadingCount] = useState(true);

  /**
   * Preload viewer count when modal opens
   * Auto-fetch viewers when Manage tab is opened
   */
  useEffect(() => {
    if (isOpen) {
      // Preload count immediately when modal opens
      refreshViewers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && tab === 'manage') {
      refreshViewers();
    }
  }, [isOpen, tab]);

  /**
   * Fetches current list of viewers from admin-list-viewers Edge Function
   */
  async function refreshViewers() {
    setBusy(true);
    setMsg(null);
    setIsLoadingCount(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        setMsg('Not authenticated');
        setBusy(false);
        setIsLoadingCount(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('admin-list-viewers', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (error) {
        setMsg(error.message);
      } else {
        // Filter out admin accounts - only show viewers
        // Role can be 'viewer', 'admin', or 'no_role' (string)
        const viewersOnly = (data?.viewers ?? []).filter((r: ViewerRow) => {
          const role = (r.role || '').toLowerCase();
          return role === 'viewer';
        });
        setRows(viewersOnly);
      }
    } catch (error) {
      setMsg('Failed to fetch viewers');
    } finally {
      setBusy(false);
      setIsLoadingCount(false);
    }
  }

  /**
   * Sends magic link invitation via invite-user Edge Function
   * Includes 10-second timeout to prevent infinite "Sending..." state
   */
  async function sendInvite() {
    if (!email.trim()) {
      setMsg('Please enter an email');
      return;
    }

    setBusy(true);
    setMsg(null);
    
    const timeoutId = setTimeout(() => {
      if (busy) {
        setMsg('Request timed out. Please try again.');
        setBusy(false);
      }
    }, 10000);

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        setMsg('Not authenticated');
        setBusy(false);
        clearTimeout(timeoutId);
        return;
      }

      logger.debug('Sending invite', { email: email.trim().toLowerCase() }, 'InviteViewerModal');
      
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: { email: email.trim().toLowerCase(), role: 'viewer' },
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      logger.debug('Invite result', { data, error }, 'InviteViewerModal');

      if (error) {
        logger.error('Invite error', { error }, 'InviteViewerModal');
        setMsg(`Error: ${error.message || 'Failed to send invite'}`);
      } else {
        setMsg('Invite sent successfully!');
        setEmail('');
        refreshViewers(); // Refresh the list
      }
    } catch (error) {
      logger.error('Invite exception', { error }, 'InviteViewerModal');
      setMsg(`Failed to send invite: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setBusy(false);
      clearTimeout(timeoutId);
    }
  }

  /**
   * Performs user management actions (deactivate, activate, delete, resend)
   * Deletes require confirmation before execution
   */
  async function act(email: string, action: 'deactivate' | 'activate' | 'delete' | 'resend') {
    if (action === 'delete' && !confirm(`Permanently delete ${email}? This cannot be undone.`)) {
      return;
    }

    setBusy(true);
    setMsg(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        setMsg('Not authenticated');
        setBusy(false);
        return;
      }

      const { error } = await supabase.functions.invoke('admin-set-user-status', {
        body: { email, action },
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (error) {
        setMsg(error.message);
      } else {
        setMsg(`${action} completed`);
        refreshViewers();
      }
    } catch (error) {
      setMsg(`Failed to ${action}`);
    } finally {
      setBusy(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-2xl theme-surface shadow-xl border theme-border">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b theme-border">
          <div className="flex gap-2">
            <button
              className={`clickable-tab-btn px-3 py-1 rounded text-sm font-medium transition-all duration-200 cursor-pointer ${
                tab === 'invite'
                  ? 'bg-[var(--ring)] text-white shadow-md scale-105'
                  : 'theme-muted theme-text-muted hover:theme-surface hover:border-[var(--ring)]/30 hover:scale-[1.02] border border-transparent'
              }`}
              onClick={() => setTab('invite')}
            >
              <UserPlus className="w-4 h-4 inline mr-2" />
              Invite
            </button>
            <button
              className={`clickable-tab-btn px-3 py-1 rounded text-sm font-medium transition-all duration-200 cursor-pointer ${
                tab === 'manage'
                  ? 'bg-[var(--ring)] text-white shadow-md scale-105'
                  : 'theme-muted theme-text-muted hover:theme-surface hover:border-[var(--ring)]/30 hover:scale-[1.02] border border-transparent'
              }`}
              onClick={() => setTab('manage')}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Manage ({isLoadingCount ? '...' : rows.length})
            </button>
          </div>
          <button
            onClick={onClose}
            className="clickable-modal-close p-1 hover:theme-muted rounded-lg transition-all duration-200 theme-text-muted hover:theme-text hover:bg-red-500/10 dark:hover:bg-red-500/20 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {tab === 'invite' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 theme-text">
                  Viewer Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !busy && email.trim()) {
                      sendInvite();
                    }
                  }}
                  className="w-full rounded-lg border theme-border px-3 py-2 theme-surface theme-text"
                  placeholder="viewer@example.com"
                  disabled={busy}
                />
              </div>
              <button
                disabled={busy || !email.trim()}
                onClick={sendInvite}
                className="w-full rounded-lg bg-[var(--ring)] text-white py-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:opacity-80 transition-opacity"
              >
                {busy ? 'Sending…' : 'Send Magic Link'}
              </button>
              {msg && (
                <p className={`text-sm ${msg.includes('success') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {msg}
                </p>
              )}
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium theme-text">All Viewers</h3>
                <button
                  onClick={refreshViewers}
                  disabled={busy}
                  className="clickable-action-btn text-sm text-[var(--ring)] hover:underline disabled:opacity-50 transition-all duration-200 cursor-pointer hover:scale-105"
                >
                  Refresh
                </button>
              </div>
              <div className="max-h-96 overflow-auto divide-y theme-border">
                {rows.map((r) => (
                  <div key={r.email} className="py-3 flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate theme-text">{r.email}</div>
                      <div className="text-xs theme-text-muted mt-1">
                        {r.is_active ? (
                          <span className="text-green-600 dark:text-green-400">● Active</span>
                        ) : (
                          <span className="text-red-600 dark:text-red-400">● Inactive</span>
                        )}
                        {r.last_invited_at && (
                          <span className="ml-2">
                            • Last invited: {new Date(r.last_invited_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => act(r.email, 'resend')}
                        disabled={busy}
                        className="clickable-action-btn text-xs px-3 py-1 rounded bg-[color-mix(in_srgb,var(--accent-primary) 12%, var(--bg) 88%)] text-[color-mix(in_srgb,var(--accent-primary) 75%, var(--text) 25%)] hover:bg-[color-mix(in_srgb,var(--accent-primary) 18%, var(--bg) 82%)] hover:scale-105 hover:shadow-sm transition-all duration-200 disabled:opacity-50 cursor-pointer"
                        title="Resend magic link"
                      >
                        Resend
                      </button>
                      {r.is_active ? (
                        <button
                          onClick={() => act(r.email, 'deactivate')}
                          disabled={busy}
                          className="clickable-action-btn text-xs px-3 py-1 rounded bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800 hover:scale-105 hover:shadow-sm transition-all duration-200 disabled:opacity-50 cursor-pointer"
                          title="Deactivate user"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => act(r.email, 'activate')}
                          disabled={busy}
                          className="clickable-action-btn text-xs px-3 py-1 rounded bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800 hover:scale-105 hover:shadow-sm transition-all duration-200 disabled:opacity-50 cursor-pointer"
                          title="Reactivate user"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        onClick={() => act(r.email, 'delete')}
                        disabled={busy}
                        className="clickable-action-btn text-xs px-3 py-1 rounded bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800 hover:scale-105 hover:shadow-sm transition-all duration-200 disabled:opacity-50 cursor-pointer"
                        title="Permanently delete user"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {!rows.length && (
                  <div className="text-sm theme-text-muted py-8 text-center">
                    No viewers yet. Invite your first viewer from the Invite tab.
                  </div>
                )}
              </div>
              {msg && (
                <p className={`text-sm mt-3 ${msg.includes('completed') || msg.includes('Done') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {msg}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}